/**
 * Client half of the draft version check (MDRS-554 / audit RB-8).
 *
 * The editor pins the document it loaded (`session-buffer.ts`) and saves on a
 * debounce. Two tabs on one case therefore both hold a document from whenever
 * they opened, and whichever saved last used to win — the other doctor's work
 * disappeared with no error shown anywhere.
 *
 * Each save now carries the version the tab believes is current, and the server
 * refuses the write if it isn't. These helpers are the three places that
 * belief is formed, carried and invalidated, kept pure so the state machine can
 * be tested without mounting the editor:
 *
 *   `versionToSend`      what to put on the wire
 *   `nextVersion`        how to advance after a save lands
 *   `parseDraftConflict` how to recognise the refusal
 *
 * The threading matters more than it looks: `runSave` makes TWO versioned
 * writes back to back (the module payload, then the .docx body). The second
 * must carry the version the FIRST returned, or the tab conflicts with itself
 * on every single autosave tick.
 */

/** Discriminator the backend puts in the 409 body. */
export const DRAFT_CONFLICT_CODE = "DRAFT_VERSION_CONFLICT";

export interface DraftConflict {
  /** The version the server actually holds — what a reload would fetch. */
  currentVersion: number;
  /** When the winning save landed, ISO-8601, or null if the server didn't say. */
  savedAt: string | null;
}

/**
 * Error thrown by the save mutations when the server refuses a stale write.
 *
 * A typed error rather than a message, because the two save paths report
 * failure in different shapes and the conflict must survive both without the
 * consumer re-parsing prose.
 */
export class DraftConflictError extends Error {
  readonly conflict: DraftConflict;

  constructor(conflict: DraftConflict) {
    super("This report was changed somewhere else.");
    this.name = "DraftConflictError";
    this.conflict = conflict;
  }
}

/**
 * The version to send with the next save, or null to send none.
 *
 * `null` means "this tab has never seen a version" — a brand-new case whose
 * draft row doesn't exist yet. Sending nothing there is deliberate: there is
 * no concurrent state to protect, and demanding a version would make the very
 * first save of every report fail.
 *
 * A known version of 0 is NOT null — every draft that predates this change has
 * a NULL column, reads as 0, and must still be protected.
 */
export const versionToSend = (known: number | null | undefined): number | null =>
  typeof known === "number" ? known : null;

/**
 * Advance the local token from a save response.
 *
 * Falls back to the previous value when the response carries no version, so a
 * non-versioned endpoint (or an older backend mid-deploy) can't reset the tab
 * to "I know nothing" and silently disable the check.
 */
export const nextVersion = (
  prev: number | null,
  response: { draft_version?: number | null } | null | undefined
): number | null => {
  const fresh = response?.draft_version;
  return typeof fresh === "number" ? fresh : prev;
};

/** Shape of the FastAPI 409 body this feature emits. */
type ConflictDetail = {
  code?: unknown;
  current_version?: unknown;
  saved_at?: unknown;
};

const fromDetail = (detail: ConflictDetail | null): DraftConflict | null => {
  if (!detail || detail.code !== DRAFT_CONFLICT_CODE) return null;
  return {
    currentVersion:
      typeof detail.current_version === "number" ? detail.current_version : 0,
    savedAt: typeof detail.saved_at === "string" ? detail.saved_at : null,
  };
};

/**
 * Recognise a save failure as a version conflict.
 *
 * Handles every shape the two save paths can produce:
 *   - a `DraftConflictError` already classified upstream
 *   - the generated client's `{ error: { detail: {...} } }` response object
 *   - the hand-rolled draft-docx `fetch`, which throws an Error whose message
 *     embeds the raw response text — the JSON has to be dug back out of prose
 *
 * Returns null for anything else, so an ordinary network failure is never
 * mistaken for a conflict and doesn't strand the user behind a dialog telling
 * them to reload.
 */
export const parseDraftConflict = (error: unknown): DraftConflict | null => {
  if (error instanceof DraftConflictError) return error.conflict;

  if (!error || typeof error !== "object") return null;

  const direct = fromDetail((error as { detail?: ConflictDetail }).detail ?? null);
  if (direct) return direct;

  const nested = fromDetail(
    ((error as { error?: { detail?: ConflictDetail } }).error?.detail ??
      null) as ConflictDetail | null
  );
  if (nested) return nested;

  const message = (error as { message?: unknown }).message;
  if (typeof message !== "string") return null;
  if (!message.includes(DRAFT_CONFLICT_CODE)) return null;
  // The marker is present, so this IS a conflict. Everything below is an
  // attempt to recover the detail — failing to parse it must still report the
  // conflict, because returning null would send the tab straight back into the
  // autosave loop it was just refused from, re-firing the same doomed save.
  const unknown: DraftConflict = { currentVersion: 0, savedAt: null };
  const start = message.indexOf("{");
  if (start === -1) return unknown;
  try {
    const parsed = JSON.parse(message.slice(start));
    return (
      fromDetail(parsed?.detail ?? null) ??
      fromDetail(parsed as ConflictDetail) ??
      unknown
    );
  } catch {
    return unknown;
  }
};

/**
 * Identity of "the thing the version token belongs to".
 *
 * Both halves are load-bearing. `ReportBuilderEditorProvider` is mounted in
 * `<Layout/>` — the app shell — so it is NOT remounted when the user navigates
 * from one case's report to another's: without the report in this key, report
 * B inherits report A's token and its first autosave is refused. And without
 * the reload nonce, a conflict reload can never re-seed. A guard missing
 * either one is a false "someone else saved this report" dialog on a report
 * nobody else has touched, followed by the user discarding their own work.
 */
export const versionSeedKey = (
  caseNumber: string | null,
  templateId: string | null,
  reloadNonce: number
): string => `${caseNumber ?? ""}|${templateId ?? ""}|${reloadNonce}`;

/**
 * Whether the tab should (re-)adopt the server's version as its own token.
 *
 * Pure because the three ways to get this wrong are all invisible in review:
 *
 * 1. **Re-seeding during normal operation.** `/draft-status` is invalidated
 *    after every save, so its value is a snapshot from *before* the save that
 *    just advanced our token. Adopting it would send a version the server has
 *    already moved past. Hence seeding once per `seedKey`, then letting
 *    `runSave` own the token.
 *
 * 2. **Not re-seeding when the session legitimately changes** — a different
 *    report, or a post-conflict reload. Hence `versionSeedKey` covering both.
 *
 * 3. **Re-seeding before the server's answer has actually arrived.** React
 *    Query keeps the previous `data` while a refetch is in flight, so a reload
 *    that seeds immediately adopts the PRE-conflict version this tab was just
 *    refused on. `isFetching` covers the in-flight case — but a refetch the
 *    client has PAUSED (it believes it is offline) is not "fetching", and a
 *    query that has never settled exposes `data: undefined` with
 *    `isFetching: false`. Seeding in either state marks the key seeded off a
 *    value that isn't the server's, and the guard then refuses to correct
 *    itself when the real data lands — so the next save conflicts again with
 *    no way out. Reloading on a flaky connection is exactly when this matters.
 *
 * A null-ref check catches none of them: it is false for the whole of normal
 * operation (so neither a reload nor a report switch would re-seed) and true
 * on the render a reload happens (when the cache is still stale).
 */
export const shouldSeedVersion = ({
  isFetching,
  isPaused,
  hasLoaded,
  seededFor,
  seedKey,
  serverVersion,
}: {
  isFetching: boolean;
  /** The client paused the refetch because it believes it is offline. */
  isPaused: boolean;
  /** The query has settled successfully at least once. */
  hasLoaded: boolean;
  seededFor: string | null;
  seedKey: string;
  serverVersion: number | null;
}): boolean => {
  // Only seed from data we can believe: settled, not in flight, not parked
  // waiting for the network. Declining is always safe — the key stays
  // unseeded and the effect runs again when the query recovers.
  if (isFetching || isPaused || !hasLoaded) return false;
  if (seededFor === seedKey) return false;
  return typeof serverVersion === "number";
};
